import React, { useState, useEffect, useRef } from "react";

import { isMobile } from "react-device-detect";
import { FollowButton } from "../../../profile/FollowButton";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import { resolveSupportContact, openSupportChannel } from "../../../utils/supportContact";

import { objectEmpty, capitalizeFirstLetter } from "../../../utils/utils.js";
import MilitaryTechOutlinedIcon from "@mui/icons-material/MilitaryTechOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import ModeOfTravelOutlinedIcon from "@mui/icons-material/ModeOfTravelOutlined";
import { Stack } from "@mui/system";
import ReactPlayer from "react-player";
import { getUserReviews, getUserExtraMedia, getUserById } from "../../../services/user";
import { getUserPastTrips } from "../../../services/trip";
import LockIcon from "@mui/icons-material/Lock";
import ExpandCircleDownOutlinedIcon from "@mui/icons-material/ExpandCircleDownOutlined";
import "./GroupDetails.scss";
import { ImageResizable } from "../ImageResizeable.js";
import { useTranslation } from "react-i18next";
import zIndex from "@mui/material/styles/zIndex.js";
import { canRouteToReserve } from "../../../utils/tripStatus";
import { useNavigate } from "react-router-dom";
import { useGlobalState } from "../../../GlobalStates";
import { useUserLocale } from "../../../customHooks/useUserLocale";

export const ProfileCard = ({ trip, tripStats, handlers, friend_id, form_topic }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [user] = useGlobalState("user");
  const { community } = useUserLocale();

  const [userPastTrips, setUserPastTrips] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [extraMedia, setExtraMedia] = useState([]);
  const [friend, setFriend] = useState(null);
  const [showUserId, setShowUserId] = useState();
  const [friendIsTripLeader, setFriendIsTripLeader] = useState(false);

  const [openBookToViewNotification, setOpenBookToViewNotification] = useState(false);

  const handleSelectUserImage = (profile_image_link) => {
    if (trip.coordinator_list && trip.coordinator_list.length > 0) {
      trip.coordinator_list.forEach((coordinator) => {
        if (coordinator.coordinator_image === profile_image_link) {
          setShowUserId(coordinator.coordinator_id);
        }
      });
    }
  };

  useEffect(() => {
    const getReviewsAndMedia = async (user_id) => {
      try {
        const res = await getUserReviews(user_id);
        const mediaRes = await getUserExtraMedia(user_id);
        setReviews(res.data);
        setExtraMedia(mediaRes.data);
      } catch (err) {
        console.log("failed to get user reviews", err);
      }
    };

    const getUserPastSotravelTrips = async (user_id) => {
      const [pastTrips, a, b] = await getUserPastTrips(user_id);
      setUserPastTrips(pastTrips);
    };

    const getFriend = async (user_id) => {
      const userRes = await getUserById(user_id);
      setFriend(userRes.data);
    };

    if (showUserId) {
      getReviewsAndMedia(showUserId);
      getUserPastSotravelTrips(showUserId);
      getFriend(showUserId);
    } else if (friend_id) {
      getReviewsAndMedia(friend_id);
      getUserPastSotravelTrips(friend_id);
      getFriend(friend_id);
    } else if (trip.best_participant_id) {
      getFriend(trip.best_participant_id);
      getReviewsAndMedia(trip.best_participant_id);
      getUserPastSotravelTrips(trip.best_participant_id);
    } else if (trip.coordinator_list && trip.coordinator_list.length > 0) {
      getFriend(trip.coordinator_list[0]);
      getReviewsAndMedia(trip.coordinator_list[0]);
      getUserPastSotravelTrips(trip.coordinator_list[0]);
    }
  }, [showUserId]);

  useEffect(() => {
    if (friend?.id && trip.coordinator_list && trip.coordinator_list.length > 0) {
      trip.coordinator_list.forEach((coordinator) => {
        if (coordinator.coordinator_id === friend.id) {
          setFriendIsTripLeader(true);
        }
      });
    }
  }, [friend]);

  const carouselReviewRef = useRef(null);
  const scrollReviewLeft = () => {
    carouselReviewRef.current.scrollLeft -= 280; // Adjust the scroll distance as needed
  };
  const scrollReviewRight = () => {
    carouselReviewRef.current.scrollLeft += 280; // Adjust the scroll distance as needed
  };
  const carouselMediaRef = useRef(null);
  const scrollMediaLeft = () => {
    carouselMediaRef.current.scrollLeft -= 280; // Adjust the scroll distance as needed
  };
  const scrollMediaRight = () => {
    carouselMediaRef.current.scrollLeft += 280; // Adjust the scroll distance as needed
  };

  const displayExtraMedia = (url, type) => {
    if (type === "image") {
      return (
        <img
          src={url}
          style={{
            height: "100%",
            width: "100%",
            objectFit: "cover",
            borderRadius: "10px",
          }}
        />
      );
    } else if (type === "video") {
      return <ReactPlayer url={url} controls playing={false} width="100%" height="100%" />;
    }
  };

  const [reviewMoreIndex, setReviewMoreIndex] = useState(-1);

  const reviewMaxLength = 120;
  const shortenText = (text) => {
    if (text.length === 0) {
      const positiveWords = ["Awesome!", "Great!", "Amazing!", "Fantastic!"];
      return positiveWords[Math.floor(Math.random() * positiveWords.length)];
    }
    if (text.length > reviewMaxLength) {
      return `"${text.slice(0, reviewMaxLength)} ...`;
    }
    return `"${text}"`;
  };

  const getBarLength = (isMobile, percentage) => {
    const fullLengthMobile = 12;
    const fullLengthDesktop = 27;

    if (isMobile) {
      return Math.ceil((percentage / 100) * fullLengthMobile) + "rem";
    } else {
      return Math.ceil((percentage / 100) * fullLengthDesktop) + "rem";
    }
  };

  if (friend)
    return (
      <>
        <div
          {...handlers}
          style={{
            width: "100%",
          }}
        >
          <div
            className="TripDetailsGroupImageWrapper"
            style={isMobile ? { marginTop: "4rem" } : { marginTop: "1rem" }}
          >
            <div className="TripDetailsGroupImageContainer">
              {trip.three_user_images && (
                <>
                  {friend && (
                    <ImageResizable
                      key="friend"
                      className="TripDetailsGroupImage"
                      style={{
                        borderColor:
                          "rgba(0, 191, 255, 0.4) rgba(0, 136, 204, 0.4) rgba(0, 85, 153, 0.4)",
                        borderWidth: "5px",
                        borderStyle: "solid",
                        zIndex: "5",
                      }}
                      src={friend.image}
                      size="m"
                    />
                  )}
                  {trip.three_user_images
                    .filter((imageUrl) => !friend || imageUrl !== friend.image)
                    .slice(0, friend ? 2 : 3)
                    .map((imageUrl, index) => (
                      <ImageResizable
                        key={index}
                        className="TripDetailsGroupImage"
                        src={imageUrl}
                        style={{
                          marginLeft: "-20px",
                          height: "6rem",
                          width: "6rem",
                          zIndex: `${4 - index}`,
                        }}
                        size="m"
                        onClick={() => handleSelectUserImage(imageUrl)}
                      />
                    ))}
                </>
              )}
            </div>
            <button
              style={{
                textAlign: "center",
                width: "100%",
                margin: "0",
                fontWeight: "600",
                textDecoration: "underline",
                fontSize: "15px",
                color: "grey",
              }}
              onClick={() => {
                setOpenBookToViewNotification(true);
              }}
            >
              {t("see_all_participants")}
              <LockIcon
                style={{
                  marginLeft: "4px",
                  fontSize: "15px",
                  fontWeight: "bold",
                }}
              />
            </button>
            {friend && (
              <Stack
                direction="row"
                justifyContent="center"
                alignItems="center"
                spacing={1}
                style={{
                  marginTop: "15px",
                }}
              >
                <FollowButton profile={friend} />
                {/* The group-chat / ask-coordinator buttons are retired —
                    questions go to Telegram/WhatsApp/LINE on the web and the
                    Sotravel Team chat in the app. */}
                <button
                  className="tw-mx-[5px] tw-mt-[1rem] tw-flex tw-h-[2rem] tw-min-h-[2rem] tw-min-w-[120px] tw-flex-row tw-items-center tw-justify-center tw-gap-[5px] tw-rounded-[5px] tw-bg-[rgb(239,239,239)] tw-px-[18px] tw-py-0 tw-text-[14px] tw-font-bold tw-leading-[2rem] tw-text-black"
                  onClick={() => {
                    const { supportLink } = resolveSupportContact(user, community);
                    openSupportChannel(supportLink);
                  }}
                >
                  <span className="tw-leading-[2rem]">{t("contact_us")}</span>
                </button>
              </Stack>
            )}
          </div>
          <div className="TripDetailsCFOContainer">
            {friend?.influencer_type === "Popular duck" ? (
              <span className="TripDetailsCFOHeader">
                {t("special_guest_name", {
                  first_name: friend.display_name,
                })}
              </span>
            ) : friend && friend_id ? (
              // special case where they viewing friend profile from club
              <span className="TripDetailsCFOHeader">{friend.display_name}</span>
            ) : friendIsTripLeader ? (
              <span className="TripDetailsCFOHeader">
                {t("flock_leader")} {friend.display_name}
              </span>
            ) : (
              <span className="TripDetailsCFOHeader">
                {t("meet_person", {
                  coordinator_name: friend.display_name,
                })}
              </span>
            )}
          </div>
          <div className="TripDetailsCFODescContainer">
            <span className="TripDetailsCFODesc" style={isMobile ? { width: "97%" } : {}}>
              {friend.description}
            </span>
          </div>

          {friendIsTripLeader && (
            <>
              <div
                className="TripDetailsSmallerHeaderContainer"
                style={isMobile ? { marginTop: "1rem" } : {}}
              >
                <span className="TripDetailsSmallerHeader">
                  {t("during_your_trip.title", {
                    defaultValue: "During your trip",
                  })}
                </span>
              </div>
              <div className="TripDetailsCFODescContainer">
                <span className="TripDetailsCFODesc" style={isMobile ? { width: "97%" } : {}}>
                  {friend.host_type === "Skilled duck"
                    ? t("during_your_trip.description_resident_host", {
                        coordinator_name: friend.display_name,
                      })
                    : friend.host_type === "Super duck"
                      ? t("during_your_trip.description_super_host", {
                          coordinator_name: friend.display_name,
                        })
                      : t("during_your_trip.description_admin", {
                          coordinator_name: friend.display_name,
                        })}
                </span>
              </div>
            </>
          )}

          <Stack className="TripDetailsHostExtraInfoContainer" spacing={1}>
            {userPastTrips.length > 0 && (
              <div className="TripDetailsHostExtraInfoRow">
                <ModeOfTravelOutlinedIcon style={{ marginRight: "3px" }} />
                <span>
                  {t("previously_attended", {
                    defaultValue: "Previously attended:",
                  })}
                  :{" "}
                  {userPastTrips
                    .slice(-6)
                    .map((userTrip) => userTrip.name)
                    .join(", ")}
                </span>{" "}
              </div>
            )}

            {friend.obsessed_with && (
              <div className="TripDetailsHostExtraInfoRow">
                <FavoriteBorderOutlinedIcon />{" "}
                <span>
                  {t("obsessed_with")}: {friend.obsessed_with}
                </span>
              </div>
            )}
            {friend.certifications && (
              <div className="TripDetailsHostExtraInfoRow">
                <ArticleOutlinedIcon />{" "}
                <span>
                  {t("certifications")}: {friend.certifications}
                </span>
              </div>
            )}
            {friend.school_and_subject && (
              <div className="TripDetailsHostExtraInfoRow">
                <SchoolOutlinedIcon />{" "}
                <span>
                  {t("school")}: {friend.school_and_subject}
                </span>
              </div>
            )}
            {friend.work_and_role && (
              <div className="TripDetailsHostExtraInfoRow">
                <WorkOutlineOutlinedIcon />{" "}
                <span>
                  {t("work")}: {friend.work_and_role}
                </span>
              </div>
            )}
          </Stack>

          {reviews.length > 0 && (
            <div
              className="TripDetailsArrowHeaderContainer"
              style={isMobile ? { marginTop: "1rem" } : {}}
            >
              <span className="TripDetailsArrowHeader">
                {t("person_reviews", {
                  defaultValue: `${friend.display_name}'s reviews`,
                  first_name: friend.display_name,
                })}
              </span>
              <Stack
                className="TripDetailslArrowContainer"
                flexDirection="row"
                justifyContent="space-between"
              >
                <button onClick={scrollReviewLeft}>
                  <ExpandCircleDownOutlinedIcon className="TripDetailsArrow left" />
                </button>

                <button onClick={scrollReviewRight}>
                  <ExpandCircleDownOutlinedIcon className="TripDetailsArrow right" />
                </button>
              </Stack>
            </div>
          )}

          {reviews.length > 0 && (
            <Stack className="TripDetailsCarouselItemContainer" ref={carouselReviewRef}>
              {reviews.map((review, index) => {
                return (
                  <div
                    className="TripDetailsCarouselItemBox"
                    style={
                      reviewMoreIndex === index
                        ? {
                            minWidth: review.positive.length * 2.1,
                          }
                        : {}
                    }
                  >
                    <span>
                      {reviewMoreIndex === index
                        ? `"${review.positive}"`
                        : shortenText(review.positive)}{" "}
                      {review.positive.length > reviewMaxLength && reviewMoreIndex !== index && (
                        <span
                          style={{ fontSize: "15px", fontWeight: "bold" }}
                          onClick={() => setReviewMoreIndex(index)}
                        >
                          more
                        </span>
                      )}
                    </span>
                    <Stack
                      flexDirection="row"
                      alignItems="center"
                      className="TripDetailsReviewImgWrapper"
                    >
                      <div className="TripDetailsReviewImgContainer">
                        <img src={review.review_by_image} />
                      </div>
                      <span>{review.review_by_display_name || review.review_by_first_name}</span>
                    </Stack>
                  </div>
                );
              })}
            </Stack>
          )}

          {extraMedia.length > 0 && (
            <div
              className="TripDetailsArrowHeaderContainer"
              style={isMobile ? { marginTop: "1rem" } : {}}
            >
              <span className="TripDetailsArrowHeader">
                {t("person_in_action", {
                  defaultValue: `${friend.display_name} in action`,
                  first_name: friend.display_name,
                })}
              </span>
              <Stack
                className="TripDetailslArrowContainer"
                flexDirection="row"
                justifyContent="space-between"
              >
                <button onClick={scrollMediaLeft}>
                  <ExpandCircleDownOutlinedIcon className="TripDetailsArrow left" />
                </button>

                <button onClick={scrollMediaRight}>
                  <ExpandCircleDownOutlinedIcon className="TripDetailsArrow right" />
                </button>
              </Stack>
            </div>
          )}

          {extraMedia.length > 0 && (
            <Stack className="TripDetailsCarouselItemContainer" ref={carouselMediaRef}>
              {extraMedia.map((media, index) => (
                <div className="TripDetailsCarouselMediaBox">
                  <div className="TripDetailsCarouselVideoBox">
                    {displayExtraMedia(media.media_url, media.media_type)}
                  </div>
                </div>
              ))}
            </Stack>
          )}

          <Modal
            open={openBookToViewNotification}
            onClose={() => {
              setOpenBookToViewNotification(false);
              setTimeout(() => {
                const groupStatsElement = document.getElementById(`group-stats-start`);

                if (trip.num_slots_paid > 2) {
                  groupStatsElement.scrollIntoView({
                    behavior: "smooth",
                  });
                }
              }, 250);
            }}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
          >
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                height: 350,
                width: 350,
                bgcolor: "white",
                borderRadius: 5,
                p: 4,
                overflowY: "scroll",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-around",
                  alignItems: "center",
                  flexDirection: "column",
                  height: "100%",
                }}
              >
                <div
                  style={{
                    borderRadius: "50%",
                    width: "130px",
                    height: "130px",
                    marginBottom: "1rem",
                    overflow: "hidden",
                  }}
                >
                  <ImageResizable
                    src={friend.image}
                    alt={friend.display_name}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                    size="s"
                  />
                </div>
                <div
                  style={{
                    color: "black",
                    fontSize: "1.2rem",
                    fontWeight: "bold",
                    width: "90%",
                  }}
                >
                  {t("book_this_trip_for_group")}
                </div>
                <button
                  style={{
                    fontFamily: "Manrope, sans-serif",
                    fontWeight: "700",
                    fontSize: "16px",
                    color: "white",
                    backgroundColor: "dodgerblue",
                    textTransform: "unset",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "2.7rem",
                    width: "9rem",
                    marginTop: "1.3rem",
                    borderRadius: "10px",
                  }}
                  onClick={() => {
                    // A sold-out / waitlist date has no bookable slots and
                    // dead-ends on the reserve page ("Please select an activity/
                    // room", Total 0). Route to the trip's date listing where the
                    // proper Waitlist action lives instead of /reserve.
                    if (canRouteToReserve(trip)) {
                      window.location.href = `/reserve?trip_id=${trip?.id}`;
                    } else {
                      window.location.href = `/trips/${trip?.project_id}`;
                    }
                  }}
                >
                  {t("book_this_trip", {
                    defaultValue: "Book this trip",
                  })}
                </button>
              </div>
            </Box>
          </Modal>
        </div>

        <div {...handlers} style={{ width: "100%", marginTop: "2rem" }}>
          {!objectEmpty(tripStats.gender_percentage) && (
            <span className="TripDetailsGroupHeader" id="group-stats-start">
              {t("about_the_group", {
                defaultValue: "About the group",
              })}
            </span>
          )}
          {!objectEmpty(tripStats.gender_percentage) && (
            <div className="TripDetailsGroupStats" style={{ marginTop: "8px" }}>
              <p className="TripDetailsGroupStatsHeader">
                {t("gender_ratio", {
                  defaultValue: "Gender ratio",
                })}
              </p>

              {Object.keys(tripStats.gender_percentage).map((key) => (
                <div className="TripDetailsGroupBarEachContainer">
                  <span className="TripDetailsGroupBarLabel">
                    {t(key, {
                      defaultValue: capitalizeFirstLetter(key),
                    })}
                  </span>
                  <div
                    className="TripDetailsGroupBar"
                    style={{
                      width: getBarLength(isMobile, tripStats.gender_percentage[key]),
                      backgroundColor: "deepskyblue",
                    }}
                  ></div>
                  <span className="TripDetailsGroupBarNumber">
                    {tripStats.gender_percentage[key]}%
                  </span>
                </div>
              ))}
            </div>
          )}

          {!objectEmpty(tripStats.age_percentage) && (
            <div className="TripDetailsGroupStats">
              <p className="TripDetailsGroupStatsHeader">{t("age_ratio")}</p>

              {Object.keys(tripStats.age_percentage).map((key) => (
                <div className="TripDetailsGroupBarEachContainer">
                  <span className="TripDetailsGroupBarLabel">
                    {t(key, {
                      defaultValue: `${key} y/o`,
                    })}
                  </span>
                  <div
                    className="TripDetailsGroupBar"
                    style={{
                      width: getBarLength(isMobile, tripStats.age_percentage[key]),
                      backgroundColor: "deepskyblue",
                    }}
                  ></div>
                  <span className="TripDetailsGroupBarNumber">
                    {tripStats.age_percentage[key]}%
                  </span>
                </div>
              ))}
            </div>
          )}

          {!objectEmpty(tripStats.skill_percentage) && (
            <div className="TripDetailsGroupStats">
              <p className="TripDetailsGroupStatsHeader">
                {t("skill_level", {
                  defaultValue: "Skill level",
                })}
              </p>
              {Object.keys(tripStats.skill_percentage).map((key) => (
                <div className="TripDetailsGroupBarEachContainer">
                  <span className="TripDetailsGroupBarLabel">
                    {t(key, {
                      defaultValue: capitalizeFirstLetter(key),
                    })}
                  </span>
                  <div
                    className="TripDetailsGroupBar"
                    style={{
                      width: getBarLength(isMobile, tripStats.skill_percentage[key]),
                      backgroundColor: "deepskyblue",
                    }}
                  ></div>
                  <span className="TripDetailsGroupBarNumber">
                    {tripStats.skill_percentage[key]}%
                  </span>
                </div>
              ))}
            </div>
          )}
          {!objectEmpty(tripStats.solo_percentage) && (
            <div className="TripDetailsGroupStats">
              <p className="TripDetailsGroupStatsHeader">
                {t("solo_travellers", {
                  defaultValue: "Solo travellers",
                })}
              </p>
              {Object.keys(tripStats.solo_percentage).map((key) => (
                <div className="TripDetailsGroupBarEachContainer">
                  <span className="TripDetailsGroupBarLabel">
                    {t(key, {
                      defaultValue: capitalizeFirstLetter(key),
                    })}
                  </span>
                  <div
                    className="TripDetailsGroupBar"
                    style={{
                      width: getBarLength(isMobile, tripStats.solo_percentage[key]),
                      backgroundColor: "deepskyblue",
                    }}
                  ></div>
                  <span className="TripDetailsGroupBarNumber">
                    {tripStats.solo_percentage[key]}%
                  </span>
                </div>
              ))}
            </div>
          )}
          {!objectEmpty(tripStats.country_percentage) && (
            <div className="TripDetailsGroupStats">
              <p className="TripDetailsGroupStatsHeader">
                {t("country", {
                  defaultValue: "Country",
                })}
              </p>
              {Object.keys(tripStats.country_percentage).map((key) => (
                <div className="TripDetailsGroupBarEachContainer">
                  <span className="TripDetailsGroupBarLabel">{capitalizeFirstLetter(key)}</span>
                  <div
                    className="TripDetailsGroupBar"
                    style={{
                      width: getBarLength(isMobile, tripStats.country_percentage[key]),
                      backgroundColor: "deepskyblue",
                    }}
                  ></div>
                  <span className="TripDetailsGroupBarNumber">
                    {tripStats.country_percentage[key]}%
                  </span>
                </div>
              ))}
            </div>
          )}
          <div style={{ paddingBottom: "3rem", width: "100%" }}></div>
        </div>
      </>
    );
};
